import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Check, X, Download, Clock, Gift, Loader2, Play, Maximize2 } from "lucide-react";

export interface PhotoCardProps {
  id: string;
  imageUrl: string;
  customerName: string;
  productName: string;
  submissionDate: string;
  status: "pending" | "approved" | "rejected";
  discountCode?: string;
  discountCodeSent?: boolean;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onDownload?: (id: string) => void;
  isProcessing?: boolean;
  className?: string;
}

const PhotoCard: React.FC<PhotoCardProps> = ({
  id,
  imageUrl,
  customerName,
  productName,
  submissionDate,
  status,
  discountCode,
  discountCodeSent,
  onApprove,
  onReject,
  onDownload,
  isProcessing = false,
  className,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const isVideo = imageUrl.toLowerCase().endsWith('.mp4') || imageUrl.toLowerCase().endsWith('.webm') || imageUrl.toLowerCase().endsWith('.mov');

  const formattedDate = new Date(submissionDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const statusColors = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-green-100 text-green-800 border-green-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
  };

  const statusIcons = {
    pending: <Clock className="w-3 h-3" />,
    approved: <Check className="w-3 h-3" />,
    rejected: <X className="w-3 h-3" />,
  };

  const MediaModal = () => {
    if (!showVideoModal && !showImageModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 md:p-8"
        onClick={() => {
          setShowVideoModal(false);
          setShowImageModal(false);
        }}
      >
        <div className="relative w-full max-w-5xl max-h-[90vh] bg-black rounded-lg overflow-hidden">
          {showVideoModal ? (
            <video
              src={imageUrl}
              className="w-full h-full object-contain"
              controls
              autoPlay
              playsInline
            />
          ) : (
            <img
              src={imageUrl}
              alt={`Photo by ${customerName}`}
              className="w-full h-full object-contain"
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => {
              setShowVideoModal(false);
              setShowImageModal(false);
            }}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={cn(
          "group relative overflow-hidden rounded-lg border bg-card",
          className
        )}
      >
        {/* Image/Video Container */}
        <div
          className="aspect-[4/3] relative cursor-pointer"
          onClick={() => {
            if (isVideo) {
              setShowVideoModal(true);
            } else {
              setShowImageModal(true);
            }
          }}
        >
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {isVideo ? (
            <div className="relative w-full h-full">
              <video
                src={imageUrl}
                className={cn(
                  "h-full w-full object-cover transition-opacity duration-300",
                  !imageLoaded && "opacity-0"
                )}
                onLoadedData={() => setImageLoaded(true)}
                muted
                loop
                playsInline
              />
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={`Photo by ${customerName}`}
              className={cn(
                "h-full w-full object-cover transition-opacity duration-300",
                !imageLoaded && "opacity-0"
              )}
              onLoad={() => setImageLoaded(true)}
            />
          )}

          {/* Preview overlay */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
            {isVideo ? (
              <Button
                variant="secondary"
                size="lg"
                className="bg-black/50 hover:bg-black/70 text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVideoModal(true);
                }}
              >
                <Play className="h-6 w-6 mr-2" />
                Play Video
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="lg"
                className="bg-black/50 hover:bg-black/70 text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageModal(true);
                }}
              >
                <Maximize2 className="h-6 w-6 mr-2" />
                View Full Size
              </Button>
            )}
          </div>

          {/* Processing indicator */}
          {isProcessing && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-6 w-6 md:h-8 md:w-8 animate-spin mx-auto mb-2 text-primary" />
                <p className="text-xs md:text-sm font-medium">Generating discount...</p>
              </div>
            </div>
          )}
        </div>

        {/* Card info section */}
        <div className="p-3 md:p-4 space-y-2 md:space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-medium text-sm md:text-base line-clamp-1">{customerName}</h3>
              {productName && (
                <p className="text-xs md:text-sm text-muted-foreground line-clamp-1">{productName}</p>
              )}
            </div>
            <div className={cn(
              "px-2 py-1 rounded-full border text-xs md:text-sm font-medium flex items-center gap-1.5",
              statusColors[status]
            )}>
              {statusIcons[status]}
              <span className="capitalize">{status}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <time className="text-xs md:text-sm text-muted-foreground">
              {formattedDate}
            </time>
            {discountCode && (
              <div className="text-xs md:text-sm font-medium text-green-600">
                Code: {discountCode}
              </div>
            )}
          </div>

          {/* Action buttons */}
          {status === "pending" && (onApprove || onReject) && !isProcessing && (
            <div className="flex gap-2 pt-2 mt-2 border-t opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <Button
                variant="default"
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => onApprove?.(id)}
                disabled={isProcessing}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="default"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => onReject?.(id)}
                disabled={isProcessing}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {/* Download button for approved photos */}
          {status === "approved" && onDownload && (
            <div className="flex pt-2 mt-2 border-t opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <Button
                variant="default"
                className="flex-1"
                onClick={() => onDownload?.(id)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          )}
        </div>
      </div>
      <MediaModal />
    </>
  );
};

export default PhotoCard;
